import { fetchAuthSession } from '@aws-amplify/auth';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import config from '../../../../../config';
import { useApplicationSyncContext } from '../../../ApplicationSyncContext/useApplicationSyncContext';
import { AudioWebhookResponse } from '../../SessionConnectionContextProvider/useSessionConnectionContext';
import useSessionInfoContext from '../../SessionInfoContextProvider/useSessionInfoContext';
import useSessionLanguageContext from '../../SessionLanguageContextProvider/useSessionLanguageContext';
import { AudioWebsocketApiConnectionContext } from './useAudioWebsocketApiConnectionContext';

export default function AudioWebsocketApiConnectionContextProvider({ children }: { children: React.ReactNode }) {
  const userQuery = useQuery(['cognito-user'], () => fetchAuthSession());
  const {
    session, setSessionId, endingSession, setEndingSession,
  } = useSessionInfoContext();
  const { sessionLanguage } = useSessionLanguageContext();
  const [audioWebsocketConnectionEnabled, setAudioWebsocketConnectionEnabled] = React.useState<boolean>(false);
  const { isSynced } = useApplicationSyncContext();

  const sessionIdRef = React.useRef<string | null>(session?.id ?? null);

  React.useEffect(() => { sessionIdRef.current = session?.id ?? null; }, [session?.id]);

  const {
    sendMessage,
    sendJsonMessage, lastJsonMessage, readyState,
  } = useWebSocket<AudioWebhookResponse>(config.Live.AudioWebsocketUrl, {
    reconnectAttempts: Infinity,
    shouldReconnect: () => true,
    onError: () => console.error('Audio websocket connection error'),
    protocols: ['cogneed-ws-protocol', userQuery.data?.tokens?.idToken?.toString()],
    onOpen: () => {
      if (session) {
        sendJsonMessage({
          type: 'settings',
          asrLanguage: sessionLanguage,
          projectId: session.projectId,
          sessionId: session.id,
          date: Date.now() * 1e6,
        });
      }
    },
    reconnectInterval(lastAttemptNumber) {
      if (lastAttemptNumber < 10) {
        return 500;
      }
      return Math.min(2 ** (lastAttemptNumber - 10) * 1000, 60000);
    },
  }, audioWebsocketConnectionEnabled && !isSynced);

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN && session?.id) {
      sendJsonMessage({
        type: 'settings',
        asrLanguage: sessionLanguage,
        projectId: session.projectId,
        sessionId: session.id,
        date: Date.now() * 1e6,
      });
    }
  }, [session?.id, session?.projectId, sessionLanguage, readyState]);

  React.useEffect(() => {
    let isMounted = true;
    if (session) {
      setAudioWebsocketConnectionEnabled(true);
    } else {
      setTimeout(() => {
        if (isMounted) {
          setAudioWebsocketConnectionEnabled(false);
        }
      }, 200);
    }
    return () => {
      isMounted = false;
    };
  }, [session]);

  React.useEffect(() => {
    if (lastJsonMessage && lastJsonMessage.type) {
      switch (lastJsonMessage.type) {
        case 'endSessionAck':
          if (sessionIdRef.current === lastJsonMessage.sessionId) {
            setSessionId(null);
            setAudioWebsocketConnectionEnabled?.(false);
            setEndingSession(null);
          }
          break;

        default:
          break;
      }
    }
  }, [lastJsonMessage]);

  React.useEffect(() => {
    if (endingSession) {
      const timeout = setTimeout(() => {
        if (endingSession === sessionIdRef.current) {
          setAudioWebsocketConnectionEnabled(false);
          setSessionId(null);
        }
      }, 1000);
      return () => {
        clearTimeout(timeout);
      };
    }
    return undefined;
  }, [endingSession, sessionIdRef]);

  const value = React.useMemo(() => ({
    sendJsonMessage,
    sendMessage,
    lastJsonMessage,
    readyState,
  }), [sendJsonMessage, sendMessage, lastJsonMessage, readyState]);

  return (
    <AudioWebsocketApiConnectionContext.Provider value={value}>
      {children}
    </AudioWebsocketApiConnectionContext.Provider>

  );
}
